// GitHub REST + GraphQL, called directly from the browser (CORS verified, spike test 1).
import { SignedOutError, type Session } from '../auth/session';

export class GitHubError extends Error {
  readonly status: number;
  constructor(message: string, status: number) { super(message); this.name = 'GitHubError'; this.status = status; }
}
export class GraphQLError extends Error {
  constructor(message: string) { super(message); this.name = 'GraphQLError'; }
}
/** GitHub returned a null payload with no `errors` (seen on addPullRequestReviewThread). */
export class NullPayloadError extends Error {
  readonly path: string;
  constructor(path: string) { super(`GraphQL payload ${path} is null`); this.name = 'NullPayloadError'; this.path = path; }
}

const API = 'https://api.github.com';

export class GitHub {
  private readonly session: Session;
  private readonly fetchFn: typeof fetch;

  constructor(session: Session, fetchFn: typeof fetch = (...a) => fetch(...a)) {
    this.session = session;
    this.fetchFn = fetchFn;
  }

  /** Authenticated fetch. On 401: refresh once and retry; if that fails, the user is signed out. */
  async #fetch(url: string, init: RequestInit = {}): Promise<Response> {
    const go = async () => this.fetchFn(url, {
      ...init,
      headers: {
        Accept: 'application/vnd.github+json',
        'X-GitHub-Api-Version': '2022-11-28',
        ...init.headers,
        Authorization: `Bearer ${await this.session.accessToken()}`,
      },
    });
    let res = await go();
    if (res.status === 401) {
      await this.session.refresh(); // throws SignedOutError when the session is gone
      res = await go();
      if (res.status === 401) throw new SignedOutError();
    }
    return res;
  }

  /** REST call. Returns `null` for 404 so callers can tell "missing" from "failed". */
  async rest<T>(path: string, init: RequestInit = {}): Promise<T | null> {
    const res = await this.#fetch(`${API}${path}`, init);
    if (res.status === 404) return null;
    if (!res.ok) throw new GitHubError(`GitHub ${init.method ?? 'GET'} ${path}: ${res.status}`, res.status);
    return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
  }

  /** Raw file contents (`Accept: application/vnd.github.raw`). Null for 404. */
  async restRaw(path: string): Promise<string | null> {
    const res = await this.#fetch(`${API}${path}`, { headers: { Accept: 'application/vnd.github.raw' } });
    if (res.status === 404) return null;
    if (!res.ok) throw new GitHubError(`GitHub GET ${path}: ${res.status}`, res.status);
    return res.text();
  }

  /** REST list endpoint, following `page` until a short page (max `maxPages`). */
  async restPaged<T>(path: string, maxPages = 30): Promise<T[]> {
    const out: T[] = [];
    for (let page = 1; page <= maxPages; page++) {
      const sep = path.includes('?') ? '&' : '?';
      const items = await this.rest<T[]>(`${path}${sep}per_page=100&page=${page}`);
      if (!items) break;
      out.push(...items);
      if (items.length < 100) break;
    }
    return out;
  }

  /**
   * GraphQL call. Throws on `errors`, and on a null value at any of the `expect`
   * dot-paths (SPEC §6: GitHub can return a null payload without `errors`).
   */
  async graphql<T>(query: string, variables: Record<string, unknown> = {}, expect: string[] = []): Promise<T> {
    const res = await this.#fetch(`${API}/graphql`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, variables }),
    });
    if (!res.ok) throw new GitHubError(`GitHub GraphQL: ${res.status}`, res.status);
    const json = (await res.json()) as { data?: unknown; errors?: { message: string }[] };
    if (json.errors?.length) throw new GraphQLError(json.errors.map((e) => e.message).join('; '));
    for (const path of expect) {
      if (getPath(json.data, path) == null) throw new NullPayloadError(path);
    }
    return json.data as T;
  }

  /** Every mutation must name at least one payload path it relies on. */
  mutate<T>(mutation: string, variables: Record<string, unknown>, expect: [string, ...string[]]): Promise<T> {
    return this.graphql<T>(mutation, variables, expect);
  }
}

function getPath(obj: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>(
    (o, k) => (o != null && typeof o === 'object' ? (o as Record<string, unknown>)[k] : undefined),
    obj,
  );
}
